import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Youtube, Twitter, Clock } from "lucide-react";
import axios from "axios";
import { API } from "../App";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_auto-parts-shop-32/artifacts/j4kogefz_download.png";

const Footer = () => {
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-info`);
        setSiteSettings(response.data);
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const categories = [
    { name: "Brakes", slug: "brakes" },
    { name: "Suspension", slug: "suspension" },
    { name: "Ignition", slug: "ignition" },
    { name: "Lighting", slug: "lighting" },
    { name: "Multimedia", slug: "multimedia" },
  ];

  const quickLinks = [
    { name: "About Us", path: "/about" },
    { name: "Contact", path: "/contact" },
    { name: "Blog", path: "/blog" },
    { name: "Shop", path: "/shop" },
  ];

  const policyLinks = [
    { name: "Returns & Refunds", path: "/returns" },
    { name: "Shipping Policy", path: "/shipping-policy" },
    { name: "Terms of Service", path: "/terms" },
    { name: "Privacy Policy", path: "/privacy" },
  ];

  // Get settings with defaults
  const phone = siteSettings?.phone || "781-870-7976";
  const email = siteSettings?.email || "contact@brendollysautoparts.com";
  const address = siteSettings?.address || "72 Chelsea Street Everett MA 02149";
  const supportHours = siteSettings?.support_hours || "Mon-Fri: 8AM-6PM, Sat: 9AM-4PM, Sun: Closed";

  return (
    <footer className="bg-gray-50 border-t border-gray-200" data-testid="footer">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img src={LOGO_URL} alt="Brendolly's Auto Parts" className="h-20 md:h-24 w-auto" />
            </Link>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Quality auto parts built on integrity, transparency, and respect. Serving car enthusiasts and professional mechanics nationwide.
            </p>
            <div className="flex gap-4 mb-6">
              <a href="#" className="text-gray-400 hover:text-primary transition-colors" data-testid="social-facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-primary transition-colors" data-testid="social-instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-primary transition-colors" data-testid="social-youtube">
                <Youtube className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-primary transition-colors" data-testid="social-twitter">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
            {/* Support Hours */}
            <div className="bg-white border border-gray-200 p-4 rounded-lg inline-block">
              <div className="flex items-center gap-2 text-primary mb-2">
                <Clock className="w-4 h-4" />
                <span className="font-semibold text-sm">Customer Support Hours</span>
              </div>
              <p className="text-gray-600 text-sm whitespace-pre-line">{supportHours}</p>
              <p className="text-gray-400 text-xs mt-2">Response within 24 hours</p>
            </div>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-gray-900 font-heading text-lg uppercase tracking-wider mb-6">Shop</h4>
            <ul className="space-y-3">
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <Link to={`/shop/${cat.slug}`} className="footer-link" data-testid={`footer-cat-${cat.slug}`}>
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links & Policies */}
          <div>
            <h4 className="text-gray-900 font-heading text-lg uppercase tracking-wider mb-6">Company</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="footer-link" data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="text-gray-900 font-heading text-lg uppercase tracking-wider mt-8 mb-6">Policies</h4>
            <ul className="space-y-3">
              {policyLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="footer-link" data-testid={`footer-policy-${link.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-gray-900 font-heading text-lg uppercase tracking-wider mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-gray-600 whitespace-pre-line">
                  {address}
                </span>
              </li>
              <li>
                <a href={`tel:${phone.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-3 text-gray-600 hover:text-primary transition-colors" data-testid="footer-phone">
                  <Phone className="w-5 h-5 text-primary" />
                  {phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${email}`} className="flex items-center gap-3 text-gray-600 hover:text-primary transition-colors" data-testid="footer-email">
                  <Mail className="w-5 h-5 text-primary" />
                  {email}
                </a>
              </li>
            </ul>
            {/* Payment Methods */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-gray-400 text-xs mb-3">Secure Payments</p>
              <div className="flex gap-2">
                <span className="bg-white border border-gray-200 px-2 py-1 text-xs rounded text-gray-600">Visa</span>
                <span className="bg-white border border-gray-200 px-2 py-1 text-xs rounded text-gray-600">MC</span>
                <span className="bg-white border border-gray-200 px-2 py-1 text-xs rounded text-gray-600">Amex</span>
                <span className="bg-white border border-gray-200 px-2 py-1 text-xs rounded text-gray-600">PayPal</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Brendolly's Auto Parts. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/privacy" className="text-gray-500 hover:text-primary transition-colors">Privacy</Link>
              <Link to="/terms" className="text-gray-500 hover:text-primary transition-colors">Terms</Link>
              <Link to="/shipping-policy" className="text-gray-500 hover:text-primary transition-colors">Shipping</Link>
              <Link to="/returns" className="text-gray-500 hover:text-primary transition-colors">Returns</Link>
            </div>
          </div>
          <p className="text-gray-400 text-xs text-center mt-4">
            Massachusetts Sales Tax (6.25%) applied to all orders shipped to MA. PCI DSS compliant. ADA accessible.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
